import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { useRoleSwitcher } from '@/features/auth/hooks';
import { useSchool } from '@/contexts/SchoolContext';
import Alert from '../ui/Alert/Alert';

const typeClasses = {
  info: 'border-info/30 bg-info/10 text-base-content',
  success: 'border-success/30 bg-success/10 text-base-content',
  warning: 'border-warning/40 bg-warning/10 text-base-content',
  error: 'border-error/30 bg-error/10 text-base-content',
};

const closeButtonClasses = 'btn btn-ghost btn-xs shrink-0';

const asDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const AnnouncementBanner = () => {
  const { currentRole } = useRoleSwitcher();
  const { schoolId } = useSchool();
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);

  // Load dismissed IDs from localStorage on mount
  useEffect(() => {
    const dismissed = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
    setDismissedIds(dismissed);
  }, []);

  // Subscribe to active announcements
  useEffect(() => {
    if (!schoolId) {
      setAnnouncements([]);
      return undefined;
    }

    const announcementsRef = collection(db, 'announcements');
    const q = query(
      announcementsRef,
      where('schoolId', '==', schoolId),
      where('active', '==', true)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedAnnouncements = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAnnouncements(fetchedAnnouncements);
    }, (error) => {
      console.error('Error fetching announcements:', error);
    });

    return () => unsubscribe();
  }, [schoolId]);

  // Filter announcements by role and dismissed status
  const visibleAnnouncements = announcements.filter(announcement => {
    // Check if dismissed
    if (dismissedIds.includes(announcement.id)) {
      return false;
    }

    const endsAt = asDate(announcement.endsAt);
    if (endsAt && endsAt < new Date()) {
      return false;
    }

    // Check if announcement applies to current role
    if (!announcement.roles || announcement.roles.length === 0) {
      return true; // Show to all if no roles specified
    }

    return announcement.roles.includes(currentRole);
  });

  // Handle dismissal
  const handleDismiss = (announcementId) => {
    const updated = [...dismissedIds, announcementId];
    setDismissedIds(updated);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(updated));
  };

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-base-200/30 py-2 px-4">
      <div className="max-w-7xl mx-auto space-y-2">
        {visibleAnnouncements.map((announcement) => (
          announcement.title || announcement.actionHref ? (
            <div
              key={announcement.id}
              className={`rounded-lg border p-4 ${typeClasses[announcement.type] || typeClasses.info}`}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex gap-4">
                  {announcement.imageUrl && (
                    <img
                      src={announcement.imageUrl}
                      alt={announcement.imageAlt || announcement.title || 'Announcement artwork'}
                      className="hidden sm:block h-20 w-16 rounded-lg object-cover border border-base-300/60 bg-base-100"
                    />
                  )}
                  <div className="min-w-0">
                    {announcement.title && (
                      <h2 className="text-sm font-semibold text-base-content">
                        {announcement.title}
                      </h2>
                    )}
                    <p className="text-sm text-base-content/75">
                      {announcement.message}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {announcement.actionHref && announcement.actionLabel && (
                    <Link
                      to={announcement.actionHref}
                      className="btn btn-primary btn-sm"
                    >
                      {announcement.actionLabel}
                    </Link>
                  )}
                  {announcement.dismissible !== false && (
                    <button
                      className={closeButtonClasses}
                      onClick={() => handleDismiss(announcement.id)}
                      aria-label="Dismiss announcement"
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Alert
              key={announcement.id}
              type={announcement.type || 'info'}
              message={announcement.message}
              onClose={announcement.dismissible !== false ? () => handleDismiss(announcement.id) : undefined}
              className="mb-0"
            />
          )
        ))}
      </div>
    </div>
  );
};

export default AnnouncementBanner;
